/**
 * 目标 与 打卡任务（habit）接口。路径见方案 4.3。
 *
 * | 方法 | 路径 |
 * | --- | --- |
 * | GET | `/students/:id/nodes?tree=1`（`kind` 可选，省略即整棵树） |
 * | POST | `/students/:id/nodes` |
 * | PATCH / DELETE | `/nodes/:nodeId` |
 * | POST | `/nodes/:nodeId/pause` `/resume` `/complete` `/stop` `/reopen` |
 * | POST | `/nodes/:nodeId/archive` `/unarchive` |
 * | POST | `/nodes/:nodeId/habits` |
 * | PATCH / DELETE | `/habits/:id` |
 *
 * ⚠️ **状态变更只能走上面那几个专用端点，不要用 `PATCH /nodes/:nodeId { status }`**：
 * 暂停 / 停止要向下传播到未完成的子目标，恢复时要区分"被父级带下来的"与
 * "用户自己手动按下的"（见 `packages/db/src/schema.ts` 的 `status_caused_by`），
 * 这些语义只有专用端点才实现得了。
 *
 * 容错：后端这些模块正在并行实现，接口未就绪（404/501/502/503）时
 * 一律降级为空树 / 空数组，页面显示"该功能尚未就绪"，而不是白屏。
 */
import { NODE_STATUSES, type NodeKind, type NodeStatus } from '@fht/shared';
import { api, tryRequest } from './client';
import { pickArray, pickObject, toNumber, toNumberArray, toOptionalNumber } from './shape';
import type { Habit, HabitWritePayload, NodeSummary, NodeWritePayload } from './types';

/** 后端 404/501 时的兜底返回：空树 + 一个标记，页面据此显示"尚未就绪"。 */
export interface NodeTreeResult {
  nodes: NodeSummary[];
  /** true = 接口未就绪（降级），页面应显示"功能尚未就绪"而不是"还没有目标"。 */
  notReady: boolean;
}

/** 把一条原始记录规整成 NodeSummary（缺字段不崩）。 */
function normalizeNode(raw: unknown, depthFallback = 1): NodeSummary | null {
  if (raw === null || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const id = typeof record.id === 'string' ? record.id : '';
  if (!id) return null;

  const kind: NodeKind = record.kind === 'task' ? 'task' : 'goal';
  // 状态解析：只认四个合法值，其余（含旧数据里的 'archived'、后端新增的未知值）
  // 一律回退到 'active' —— 宁可显示成"进行中"，也不要让整页崩掉。
  const status: NodeStatus = (NODE_STATUSES as readonly string[]).includes(record.status as string)
    ? (record.status as NodeStatus)
    : 'active';

  // 归档是**独立维度**：非 NULL 即已归档（见 packages/db/src/schema.ts 的 archived_at）。
  // 兼容后端返回 null/undefined（未归档）与空串（视为未归档）。
  const archivedAt =
    typeof record.archivedAt === 'string' && record.archivedAt ? record.archivedAt : null;

  const children = pickArray<unknown>(record, 'children', 'nodes')
    .map((child) => normalizeNode(child, depthFallback + 1))
    .filter((child): child is NodeSummary => child !== null);

  const habits = normalizeHabits(pickArray<unknown>(record, 'habits'));

  return {
    id,
    kind,
    parentId: typeof record.parentId === 'string' ? record.parentId : null,
    depth: toNumber(record.depth, depthFallback),
    name: typeof record.name === 'string' && record.name ? record.name : '未命名',
    note: typeof record.note === 'string' ? record.note : '',
    startDate: typeof record.startDate === 'string' ? record.startDate : '',
    endDate: typeof record.endDate === 'string' ? record.endDate : null,
    isLongTerm: record.isLongTerm === true,
    durationYears: toOptionalNumber(record.durationYears),
    durationMonths: toOptionalNumber(record.durationMonths),
    durationDays: toOptionalNumber(record.durationDays),
    status,
    archivedAt,
    createdByType: record.createdByType === 'student' ? 'student' : 'parent',
    createdById: typeof record.createdById === 'string' ? record.createdById : '',
    sortOrder: toOptionalNumber(record.sortOrder) ?? 0,
    progress: normalizeProgress(record.progress),
    habits,
    children,
  };
}

function normalizeProgress(raw: unknown): NodeSummary['progress'] {
  if (raw === null || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const doneValue = toNumber(record.doneValue ?? record.done, 0);
  const targetValue = toNumber(record.targetValue ?? record.target, 0);
  const rawRatio = toOptionalNumber(record.ratio);
  return {
    doneValue,
    targetValue,
    // 后端不给比例时前端按 done/target 兜底；target 为 0 表示无法计算（长期节点）
    ratio: rawRatio ?? (targetValue > 0 ? Math.min(1, doneValue / targetValue) : null),
    leafCount: toOptionalNumber(record.leafCount) ?? undefined,
    checkinCount: toOptionalNumber(record.checkinCount) ?? undefined,
    doneCount: toOptionalNumber(record.doneCount) ?? undefined,
  };
}

export function normalizeHabit(raw: unknown): Habit | null {
  if (raw === null || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const id = typeof record.id === 'string' ? record.id : '';
  if (!id) return null;

  const recurrence =
    record.recurrence === 'weekly' ||
    record.recurrence === 'monthly' ||
    record.recurrence === 'custom'
      ? record.recurrence
      : 'daily';

  return {
    id,
    nodeId: typeof record.nodeId === 'string' ? record.nodeId : '',
    name: typeof record.name === 'string' && record.name ? record.name : '未命名打卡',
    recurrence,
    // weekdays / monthDays 后端可能返回 null，统一成数组避免页面 `.map` 崩
    weekdays: toNumberArray(record.weekdays),
    monthDays: toNumberArray(record.monthDays),
    intervalDays: toOptionalNumber(record.intervalDays),
    targetValue: toOptionalNumber(record.targetValue),
    unit: typeof record.unit === 'string' ? record.unit : null,
    /**
     * N 的兜底必须是 **1**（不是 0）：老后端不返回这个字段时，
     * `每 1 单位得 M 分` 正是改动前的语义 —— 兜底成 0 会让算分变成除零。
     */
    pointsUnitSize: Math.max(1, toNumber(record.pointsUnitSize, 1)),
    pointsPerUnit: toNumber(record.pointsPerUnit, 0),
    dailyPointsCap: toOptionalNumber(record.dailyPointsCap),
    graceHours: toNumber(record.graceHours, 24),
    activeFrom: typeof record.activeFrom === 'string' ? record.activeFrom : '',
    activeTo: typeof record.activeTo === 'string' ? record.activeTo : null,
    active: record.active !== false,
    /**
     * 打卡任务自己的顺序（迁移 0021）。
     *
     * ⚠️ 2026-10-30 补：这一层以前**把 sortOrder 丢掉了**（逐字段拼对象，漏了就没了），
     * 于是「调整顺序」保存后重新进来看到的还是旧的（所有 habit 的 sortOrder 都被当成 0，
     * 列表退化成"按节点分组 + 树顺序"）—— 用户原话："他说保存了。接着我退出去，
     * 再进来，就发现还是没有保存呢"。后端其实存住了（审计里有 `habit.reorder`）。
     */
    sortOrder: toOptionalNumber(record.sortOrder) ?? 0,
    createdByType: record.createdByType === 'student' ? 'student' : 'parent',
    createdById: typeof record.createdById === 'string' ? record.createdById : '',
  };
}

function normalizeHabits(raw: unknown[]): Habit[] {
  return raw.map(normalizeHabit).filter((habit): habit is Habit => habit !== null);
}

/**
 * 拉取某学生的**节点树**。
 *
 * `kind` 现在**只有历史调用方**才传（界面已不再区分"目标 / 任务"，见
 * `ParentNodesPage`）。**省略 `kind` 就返回全部节点（一棵树）** —— 后端
 * `nodes.service.ts` 的 `loadTreeRows` 里 `kindFilter` 在缺省时为空，
 * 所以 kind='task' 的历史节点也会一起回来，不会因为不再传 kind 而消失。
 *
 * `tree=1` 时后端返回嵌套结构；万一它返回的是扁平数组，
 * 这里会**在前端按 parentId 重建树**，保证三级缩进照样显示得出来。
 */
export async function listNodeTree(studentId: string, kind?: NodeKind): Promise<NodeTreeResult> {
  const result = await tryRequest(() =>
    // `kind` 为 undefined 时 `client.buildUrl` 会跳过这个查询参数（不会发出 `kind=undefined`）
    api.get<unknown>(`/students/${studentId}/nodes`, { kind, tree: 1 }),
  );
  // 只对"接口不存在"降级；权限（403）/网络错误原样抛出，由页面显示错误态
  if (result.state === 'notReady') return { nodes: [], notReady: true };
  return { nodes: buildTree(pickArray<unknown>(result.data, 'nodes', 'tree', 'items')), notReady: false };
}

/** 把扁平列表按 parentId 组装成树（已经是树时原样规整）。 */
export function buildTree(rawList: unknown[]): NodeSummary[] {
  const flat = rawList
    .map((raw) => normalizeNode(raw))
    .filter((node): node is NodeSummary => node !== null);

  // 只有"所有节点都没有 children"才能安全地按 parentId 重建。
  // 若只有部分节点带 children（后端返回了混合结构 —— 比如只展开了前两级），
  // 强行重建会丢掉已展开的那部分，所以这种情况直接沿用后端给的嵌套结果。
  const anyNesting = flat.some((node) => (node.children?.length ?? 0) > 0);
  if (anyNesting) return sortTree(flat);

  const byId = new Map<string, NodeSummary>();
  for (const node of flat) byId.set(node.id, { ...node, children: [] });

  const roots: NodeSummary[] = [];
  for (const node of byId.values()) {
    const parent = node.parentId ? byId.get(node.parentId) : undefined;
    if (parent) {
      parent.children = [...(parent.children ?? []), node];
    } else {
      roots.push(node);
    }
  }
  return sortTree(roots);
}

function sortTree(nodes: NodeSummary[]): NodeSummary[] {
  const sorted = [...nodes].sort((a, b) => {
    const orderA = a.sortOrder ?? 0;
    const orderB = b.sortOrder ?? 0;
    if (orderA !== orderB) return orderA - orderB;
    return a.name.localeCompare(b.name, 'zh-Hans-CN');
  });
  for (const node of sorted) {
    if (node.children && node.children.length > 0) {
      node.children = sortTree(node.children);
    }
  }
  return sorted;
}

/**
 * 从"包了一层"的创建响应里取出实体。
 *
 * ⚠️ 后端创建类接口返回的是 `ok({ node })` / `ok({ habit })`
 *   （见 `apps/api/src/modules/nodes/nodes.routes.ts` 的 201 分支）。
 *   而 `pickObject(x, x)` 只在**存在嵌套 `data` 键**时才下钻，
 *   所以它会原样返回 `{ node: {...} }`；随后 `normalizeNode` 读 `id`/`name`
 *   全是 undefined → 返回 null → 前端抛「服务端返回的节点数据无法解析」。
 *
 * 后果极具误导性：**实体其实已经创建成功**（列表里立刻能看到），
 * 但界面弹一条红色错误，用户会以为失败并**重复创建**一遍。
 * 这是"响应形状猜错"这一类问题的又一个实例。
 */
function unwrapEntity(data: unknown, key: string): unknown {
  if (data !== null && typeof data === 'object' && !Array.isArray(data)) {
    const value = (data as Record<string, unknown>)[key];
    if (value !== null && typeof value === 'object') return value;
  }
  // 兼容后端改成"平铺返回实体本身"的情况
  return data;
}

/** 新建目标。 */
export async function createNode(
  studentId: string,
  payload: NodeWritePayload & { kind: NodeKind; parentId: string | null },
): Promise<NodeSummary> {
  const data = await api.post<unknown>(`/students/${studentId}/nodes`, payload);
  const node = normalizeNode(unwrapEntity(data, 'node'));
  if (!node) throw new Error('服务端返回的节点数据无法解析');
  return node;
}

/** 编辑目标。 */
export async function updateNode(nodeId: string, payload: NodeWritePayload): Promise<void> {
  await api.patch(`/nodes/${nodeId}`, payload);
}

/**
 * **拖放排序（打卡任务级）**：按数组顺序重写 `habit.sort_order`（2026-10-30）。
 *
 * 孩子端「今天要做什么」按这一列排（`studentToday` 的 ORDER BY），
 * 所以拖完孩子那边立刻就是新顺序。只有家长能调（服务端判）。
 *
 * 返回服务端实际写入的顺序 —— 界面用它校对（不靠本地猜）。
 */
export async function reorderHabits(
  studentId: string,
  ids: readonly string[],
): Promise<{ updated: number; order: { id: string; sortOrder: number }[] }> {
  const data = await api.put<unknown>(`/students/${studentId}/habits/order`, { ids: [...ids] });
  const record = (data ?? {}) as Record<string, unknown>;
  const rawOrder = Array.isArray(record.order) ? record.order : [];
  return {
    updated: toNumber(record.updated, 0),
    order: rawOrder
      .map((item) => item as Record<string, unknown>)
      .filter((item) => typeof item.id === 'string')
      .map((item) => ({ id: String(item.id), sortOrder: toNumber(item.sortOrder, 0) })),
  };
}

// ---------------------------------------------------------------- 状态变更 / 归档
//
// 统一形状：`POST /nodes/:id/<action>`（无请求体）→ `{ node }`。
// 返回解析后的 NodeSummary，调用方可以直接拿新状态做提示，不用再猜。

/** 全部状态类动作的路径片段。集中在这里，避免每个函数各写一遍字符串。 */
export type NodeStatusAction = 'pause' | 'resume' | 'complete' | 'stop' | 'reopen';
type NodeArchiveAction = 'archive' | 'unarchive';

async function postNodeAction(
  nodeId: string,
  action: NodeStatusAction | NodeArchiveAction,
): Promise<NodeSummary> {
  /**
   * 每个动作的路径都**写成字面量**（而不是拼 `action` 变量）：
   * `scripts/check-api-paths.mjs` 用正则在源码里抽取调用路径，
   * 拼出来的 `/nodes/${nodeId}/${action}` 会被它读成 `/nodes/{}/{}`，
   * 于是这 7 条调用在自检里全都匹配不上后端路由 —— 那种"绿转红"的噪音
   * 会掩盖真正的路径写错。字面量写法让自检能逐条核对。
   */
  const data = await (async (): Promise<unknown> => {
    switch (action) {
      case 'pause':
        return api.post<unknown>(`/nodes/${nodeId}/pause`);
      case 'resume':
        return api.post<unknown>(`/nodes/${nodeId}/resume`);
      case 'complete':
        return api.post<unknown>(`/nodes/${nodeId}/complete`);
      case 'stop':
        return api.post<unknown>(`/nodes/${nodeId}/stop`);
      case 'reopen':
        return api.post<unknown>(`/nodes/${nodeId}/reopen`);
      case 'archive':
        return api.post<unknown>(`/nodes/${nodeId}/archive`);
      case 'unarchive':
        return api.post<unknown>(`/nodes/${nodeId}/unarchive`);
      default:
        return null;
    }
  })();
  // 后端按约定返回 `ok({ node })`；用 unwrapEntity 兼容"平铺返回实体本身"的写法。
  const node = normalizeNode(unwrapEntity(data, 'node'));
  if (!node) throw new Error('服务端返回的节点数据无法解析');
  return node;
}

/** 通用入口：`POST /nodes/:id/<action>`（页面按动作调用，见 domainQueries 的 setStatus）。 */
export async function changeNodeStatus(
  nodeId: string,
  action: NodeStatusAction,
): Promise<NodeSummary> {
  return postNodeAction(nodeId, action);
}

/**
 * 暂停（会向下传播：其下未完成的子目标一起暂停，已完成的保持不变）。
 *
 * 为什么不用 `PATCH { status:'paused' }`：传播与"被谁带下来的"还原逻辑
 * 只在专用端点里实现（见文件头注释）。
 */
export async function pauseNode(nodeId: string): Promise<NodeSummary> {
  return postNodeAction(nodeId, 'pause');
}

/** 恢复（连同之前"被这次暂停带下去"的子目标一起恢复）。 */
export async function resumeNode(nodeId: string): Promise<NodeSummary> {
  return postNodeAction(nodeId, 'resume');
}

/**
 * 完成。**可能被服务端拒绝**：还有未完成的子目标时返回中文提示
 * （形如"还有 N 个子目标未完成"），调用方必须用 `toErrorMessage` 原样展示，不要吞掉。
 */
export async function completeNode(nodeId: string): Promise<NodeSummary> {
  return postNodeAction(nodeId, 'complete');
}

/** 停止（会向下传播：其下未完成的子目标一起停止，已完成的保持不变）。 */
export async function stopNode(nodeId: string): Promise<NodeSummary> {
  return postNodeAction(nodeId, 'stop');
}

/** 重新开启（终态可逆）：回到进行中，并恢复之前被这次操作暂停/停止的子目标。 */
export async function reopenNode(nodeId: string): Promise<NodeSummary> {
  return postNodeAction(nodeId, 'reopen');
}

/**
 * 归档。归档**不再是一个状态**，而是独立字段 `node.archivedAt`（见共享常量注释）。
 * 有打卡记录时服务端会拒绝删除，引导改用归档。
 */
export async function archiveNode(nodeId: string): Promise<NodeSummary> {
  return postNodeAction(nodeId, 'archive');
}

/** 取消归档（回到原本的进展状态与列表可见性）。 */
export async function unarchiveNode(nodeId: string): Promise<NodeSummary> {
  return postNodeAction(nodeId, 'unarchive');
}

/** 删除。服务端在有打卡记录时返回 NODE_HAS_CHECKINS，页面引导改用归档。 */
export async function deleteNode(nodeId: string): Promise<void> {
  await api.del(`/nodes/${nodeId}`);
}

// ---------------------------------------------------------------- 打卡任务（habit）

/**
 * 在某叶子节点下创建打卡任务。非叶子会被服务端拒绝（NODE_NOT_LEAF）。
 *
 * ⚠️ 请求体里**必须带 `nodeId`**：`createHabitSchema` 把它定义为必填（uuid），
 *   后端还会校验它与路径参数一致（避免"以为建在这、实际建在那"）。
 *   早先这里只发了 `payload`（里面没有 nodeId），于是后端 zod 解析直接失败 ——
 *   而且那个 ZodError 当时还被错误地映射成了 **500**，界面上只显示"请求失败（HTTP 500）"，
 *   完全看不出是"少传了一个字段"。
 *   这里统一在**唯一出口**补上 nodeId，调用方不必再各自记得带。
 */
export async function createHabit(nodeId: string, payload: HabitWritePayload): Promise<Habit> {
  const data = await api.post<unknown>(`/nodes/${nodeId}/habits`, { ...payload, nodeId });
  // 后端返回 ok({ habit })，同样需要拆包（见 unwrapEntity 注释）
  const habit = normalizeHabit(unwrapEntity(data, 'habit'));
  if (!habit) throw new Error('服务端返回的打卡任务数据无法解析');
  return habit;
}

/** 编辑打卡任务（停用传 `active:false`，保留历史统计）。 */
export async function updateHabit(id: string, payload: Partial<HabitWritePayload>): Promise<void> {
  await api.patch(`/habits/${id}`, payload);
}

/** 删除打卡任务。 */
export async function deleteHabit(id: string): Promise<void> {
  await api.del(`/habits/${id}`);
}

/**
 * 单独拉某个节点的打卡任务列表（树接口没带 habits 时的兜底）。
 * 路径未在方案里写死，用 GET `/nodes/:id/habits`；404 时返回空数组。
 */
export async function listHabits(nodeId: string): Promise<Habit[]> {
  const result = await tryRequest(() => api.get<unknown>(`/nodes/${nodeId}/habits`));
  if (result.state === 'notReady') return [];
  return normalizeHabits(pickArray<unknown>(result.data, 'habits', 'items'));
}
